import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {LocalStorageProperties, UserRoles} from '../../enums/general-enums';
import {BehaviorSubject} from 'rxjs';
import {Router} from '@angular/router';
import {PAGES_AND_PATHS} from '../../constants/pages-and-paths';
import {HomepageDataHelperService} from './homepage-data-helper.service';
import {isPlatformBrowser} from '@angular/common';
import {CookieTokenService} from './cookie-token.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationHelperService {

  private isUserAuthenticated = false;
  private userData: any = null;
  private authUserRole = '';
  private token = '';
  private authStatusSubject = new BehaviorSubject<boolean>(false);
  public authStatus$ = this.authStatusSubject.asObservable();

  constructor(private router: Router,
              private homepageDataService: HomepageDataHelperService,
              @Inject(PLATFORM_ID) private platformId: string,
              private cookieService: CookieTokenService) {
    this.checkIfUserIsLogged();
  }

  get isAuthenticated(): boolean {
    return this.isUserAuthenticated;
  }

  get getAuthUserRole(): string {
    return this.authUserRole;
  }

  get getToken(): string {
    return (!isPlatformBrowser(this.platformId)) ?
      this.cookieService.getToken() ?? '' :
      this.token;
  }

  setToken(newData: any): void {
    this.token = newData;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(LocalStorageProperties.token, JSON.stringify(this.token));
    } else {
      this.cookieService.setToken(newData);
    }
  }

  get getUserData(): any {
    return this.userData;
  }

  setUserData(newData: any): void {
    this.userData = newData;
    // this.authUserRole = newData.role.name;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(LocalStorageProperties.user, JSON.stringify(this.userData));
    }
  }

  clientLogin(user: any, token: string, redirectionUrl: string): void {
    this.setUserData(user);
    this.setToken(token);
    this.cookieService.setToken(token);
    this.isUserAuthenticated = true;
    setTimeout(() => {
      if (redirectionUrl) {
        this.router.navigate([redirectionUrl]);
      }
    });
    this.authStatusSubject.next(true);
  }

  clientLogout(redirectionUrl: string): void {
    this.deleteClientInfoFromStorage();
    this.authStatusSubject.next(false);
    this.router.navigate([redirectionUrl]);
  }

  private deleteClientInfoFromStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(LocalStorageProperties.user);
      localStorage.removeItem(LocalStorageProperties.userProfile);
      localStorage.removeItem(LocalStorageProperties.token);
    }
    this.userData = null;
    this.token = '';
    this.isUserAuthenticated = false;
    this.authUserRole = '';
  }

  private checkIfUserIsLogged(): void {
    let user: any;
    let token: any;
    if (isPlatformBrowser(this.platformId)) {
      if (localStorage.getItem(LocalStorageProperties.user) !== 'undefined') {
        user = JSON.parse(localStorage.getItem(LocalStorageProperties.user) as string);
      }
      if (localStorage.getItem(LocalStorageProperties.token) !== 'undefined') {
        token = JSON.parse(localStorage.getItem(LocalStorageProperties.token) as string);
      }
    }
    if (user && !this.isUserAuthenticated) {
      this.clientLogin(user, token, '');
    }
    if (this.isUserAuthenticated && user) {
      this.authStatusSubject.next(true);
    }
  }

  checkActiveSubscription(subscriptions: Array<any>): string {
    if (subscriptions === null) {
      return UserRoles.freemium;
    }
    const activePlans = subscriptions.filter((subscription: any) => subscription.stripe_status === 'active');
    const highestId = Math.max(...activePlans.map(obj => obj.id));
    const activeSubscription = activePlans.find(obj => obj.id === highestId);

    if (activeSubscription) {
      if (['tlalister', 'tlalister-mobile'].includes(activeSubscription.type)) {
        return UserRoles.aLister;
      } else if (['tlexpert', 'tlexpert-mobile'].includes(activeSubscription.type)) {
        return UserRoles.expert;
      } else if (['tlpartner', 'tlpartner-mobile'].includes(activeSubscription.type)) {
        return UserRoles.company;
      } else {
        return UserRoles.freemium;
      }
    }
    return UserRoles.freemium;
  }


  get getUserUsername(): string {
    return this.userData?.username;
  }

  get getUserCountry(): string {
    return this.userData?.country;
  }

  get getUserPhone(): string {
    return this.userData?.phone_number;
  }
}
